/**
 * One table from every error `code` the shared-folders API returns to the text a user sees.
 *
 * Errors arrive as `{code, message}` (`shares/common.py` `ApiError`). We show our own text for each
 * code rather than the server's `message`. A few codes carry a detail only the server knows (which
 * folder overlaps, which domains are allowed, the masked address an invitation went to); for those
 * the server's message is shown, but only after {@link safeServerMessage} has checked it.
 *
 * Why not just show `message`: the app treats any error text containing "Not authorized" as an
 * expired session and signs the user out (`AuthErrorInterceptor`), and a gateway-level 403 says
 * exactly that. Nothing from the server reaches the screen without passing through here.
 *
 * Keep in sync with every `ApiError(...)` in `JobService/lambda/code/python/shares/` (the test
 * `errors.test.ts` lists them).
 */

export interface ShareErrorEntry {
  message: string
  /** Prefer the server's message, when safe, because it names something specific. */
  serverDetail?: boolean
}

/** Checks every entry's shape while keeping the codes as literal keys. */
const errorTable = <T extends Record<string, ShareErrorEntry>>(table: T): T => table

export const SHARE_ERROR_MESSAGES = errorTable({
  // Any route
  not_found: {
    message: "We couldn't find that. It may have been removed, or the folder is no longer shared.",
  },
  insufficient_access: {message: "Your access to this shared folder doesn't allow that."},
  identity_unavailable: {
    message: "Your cloud storage isn't set up yet. Open My Files once, then try again.",
  },
  internal_error: {message: 'Something went wrong. Please try again.'},
  bad_request: {message: "That request wasn't valid. Please try again."},
  concurrent_change: {message: 'Your shared folders changed at the same time. Please try again.'},
  limit_reached: {message: 'A sharing limit has been reached.', serverDetail: true},
  rate_limited: {
    message: "You've sent a lot of invitations. Please try again later.",
    serverDetail: true,
  },

  // Sharing a folder
  invalid_name: {message: 'The name must be 1 to 200 characters, without control characters.'},
  root_not_shareable: {message: "The root of My Files can't be shared. Share a folder inside it."},
  invalid_folder_path: {message: "This folder's path can't be shared."},
  unsupported_character: {
    message:
      "Folders whose path contains * ? $ quotes, backslashes or control characters can't be shared. Rename the folder first.",
  },
  subscription_required: {message: 'Sharing folders needs an active subscription.'},
  folder_not_found: {message: 'That folder no longer exists in your files.'},
  overlapping_share: {
    message: "A folder inside or above a shared folder can't be shared.",
    serverDetail: true,
  },

  // Members
  invalid_access: {message: 'Choose Can view or Can edit.'},
  owner_access_fixed: {message: "The owner's access can't be changed."},
  owner_cannot_leave: {message: "The owner can't leave a shared folder. Stop sharing it instead."},

  // Inviting
  invitee_not_in_subscription: {
    message:
      'Only people in one of your subscriptions can be invited. An owner or admin of a subscription can allow inviting others in its settings.',
  },
  invitee_has_no_email: {message: 'That member has no email address to send the invitation to.'},
  invalid_email: {message: "That email address isn't valid."},
  domain_not_allowed: {
    message: "Your subscriptions don't allow inviting addresses from that domain.",
    serverDetail: true,
  },
  cannot_invite_owner: {message: 'You already own this folder.'},
  already_member: {message: 'That person is already a member of this folder.'},
  invitee_not_in_pilot: {
    message: "Shared folders are in a pilot, and that person isn't part of it yet.",
  },
  email_failed: {message: "The invitation email couldn't be sent. Please try again later."},

  // Answering an invitation
  already_used: {message: 'This invitation has already been used.'},
  invitation_expired: {message: 'This invitation has expired. Ask the owner for a new one.'},
  invitation_withdrawn: {message: 'This invitation is no longer valid.'},
  email_mismatch: {
    message:
      'This invitation was sent to a different email address. Sign in with that address to accept it.',
    serverDetail: true,
  },
  not_in_subscription: {
    message:
      "This folder is shared with people in the owner's subscriptions, and you aren't in any of them.",
  },
  subscription_not_allowed: {
    message: "The owner's subscriptions only accept members of certain subscriptions.",
  },

  // Subscription settings
  owner_or_admin_only: {message: 'Only owners and admins of the subscription can change this.'},
  invalid_settings: {
    message: 'Check the domains and subscriptions you entered.',
    serverDetail: true,
  },

  // Files in a shared folder (P1-F3/F4 use these)
  invalid_path: {message: "That path isn't valid."},
  upload_not_found: {message: 'That upload no longer exists. Start it again.'},
  invalid_parts: {message: "The uploaded parts don't match. Start the upload again."},
  too_many_objects: {message: 'Too many files at once.', serverDetail: true},
  same_location: {message: 'The source and the destination are the same.'},
  into_itself: {message: "A folder can't be moved or copied into itself."},
  source_not_found: {message: 'Some of the items no longer exist.', serverDetail: true},
  too_large: {
    message: "That's more than can be transferred at once (1,000 files or 20 GB).",
    serverDetail: true,
  },
  access_lost: {message: 'Access to the shared folder ended during the transfer.'},
  // A transfer from My Files into a shared folder that would carry one of your own shared folders.
  shared_folder_locked: {
    message: "A shared folder can't be moved into another shared folder.",
    serverDetail: true,
  },
  // Any write in a shared folder while its owner moves it (`POST /v1/shares/moves`); reads go on.
  share_moving: {
    message:
      "This folder is being moved by its owner. You can open files; changes are paused until it's done.",
  },

  // Moving or renaming your own shared folders (`POST /v1/shares/moves`)
  nested_share: {
    message: "Shared folders can't be inside other shared folders.",
    serverDetail: true,
  },
  destination_exists: {message: 'Something with that name already exists there.'},
  move_in_progress: {
    message: 'One of your moves is still running. Try again when it has finished.',
  },
  share_busy: {
    message:
      'Files are being copied or moved in that shared folder. Try again when that has finished.',
    serverDetail: true,
  },
  overlapping_sources: {
    message: 'Some of the items are inside other selected folders. Select only the outer folder.',
  },
  duplicate_name: {message: 'Two of the items have the same name.'},
})

export type ShareErrorCode = keyof typeof SHARE_ERROR_MESSAGES

export interface ShareErrorInfo {
  /** HTTP status, or undefined when the request never got an answer. */
  status?: number
  code?: string
  serverMessage?: string
}

/** Status, code and server message from whatever Amplify (axios) threw. */
export function readShareError(err: unknown): ShareErrorInfo {
  const response = (err as {response?: {status?: unknown; data?: unknown}} | null)?.response
  const status = typeof response?.status === 'number' ? response.status : undefined
  const data = response?.data
  const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  return {
    status,
    code: typeof body.code === 'string' ? body.code : undefined,
    serverMessage: typeof body.message === 'string' ? body.message : undefined,
  }
}

export function shareErrorCode(err: unknown): string | undefined {
  return readShareError(err).code
}

export function isKnownShareErrorCode(code: string | undefined): code is ShareErrorCode {
  return !!code && Object.prototype.hasOwnProperty.call(SHARE_ERROR_MESSAGES, code)
}

/**
 * The server's message when it is fit to show: short, plain, and free of the "Not authorized"
 * phrase that would read as an expired session.
 */
export function safeServerMessage(message: string | undefined): string | undefined {
  if (!message) return undefined
  const text = message.trim()
  if (!text || text.length > 300 || /not\s+authori[sz]ed/i.test(text) || /[<>]/.test(text)) {
    return undefined
  }
  return text
}

/** Text for an error that came without a known code, by HTTP status. */
function messageForStatus(status: number | undefined): string {
  if (status === undefined) {
    return "Couldn't reach the server. Check your connection and try again."
  }
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return "You don't have access to do that."
  if (status === 404) return SHARE_ERROR_MESSAGES.not_found.message
  if (status === 409) return SHARE_ERROR_MESSAGES.concurrent_change.message
  if (status === 410) return SHARE_ERROR_MESSAGES.invitation_withdrawn.message
  if (status === 429) return SHARE_ERROR_MESSAGES.rate_limited.message
  return SHARE_ERROR_MESSAGES.internal_error.message
}

export interface ShareErrorMessageOptions {
  /** Text for a specific code in this place, e.g. "You're already a member" on the invitee side. */
  overrides?: Partial<Record<ShareErrorCode, string>>
}

/** The text to show for a failed sharing call. Never returns the raw server text unchecked. */
export function shareErrorMessage(err: unknown, options: ShareErrorMessageOptions = {}): string {
  const {status, code, serverMessage} = readShareError(err)
  if (isKnownShareErrorCode(code)) {
    const override = options.overrides?.[code]
    if (override) return override
    const entry: ShareErrorEntry = SHARE_ERROR_MESSAGES[code]
    if (entry.serverDetail) {
      return safeServerMessage(serverMessage) ?? entry.message
    }
    return entry.message
  }
  return messageForStatus(status)
}
